import React, { useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import { StandardSchemaV1Issue, useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { NameInput } from '@/features/Dashboard/components/NameInput';
import { NAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { useDashboard } from '@/hooks/api/dashboard';

const convertErrorsToStandardSchema = (
  errors: StandardSchemaV1Issue[]
): StandardSchemaV1Issue[] | undefined => {
  return errors.length > 0 ? errors : undefined;
};

interface EditDashboardNameProps {
  onValidationChange?: (hasErrors: boolean) => void;
}

const EditDashboardNameFieldContent = ({
  field,
  onValidationChange,
  setDashboardName
}: {
  field: any;
  onValidationChange?: (hasErrors: boolean) => void;
  setDashboardName: (name: string) => void;
}) => {
  const hasErrors = field.state.meta.errors.length > 0;

  useEffect(() => {
    onValidationChange?.(hasErrors);
  }, [hasErrors, onValidationChange]);

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: '255px',
        '& .MuiInputBase-root': {
          fontSize: '32px',
          lineHeight: '32px',
          fontWeight: 700
        },
        '& .MuiInputBase-input': {
          fontSize: '32px',
          lineHeight: '32px',
          fontWeight: 700,
          padding: '7px 2px 7px 8px'
        }
      }}
    >
      <NameInput
        error={convertErrorsToStandardSchema(field.state.meta.errors as StandardSchemaV1Issue[])}
        maxLength={NAME_MAX_LENGTH}
        value={field.state.value}
        onBlur={() => {
          field.handleBlur();
          if (field.state.meta.errors.length === 0 && field.state.value.trim()) {
            const trimmed = field.state.value.trim();
            setDashboardName(trimmed);
          }
        }}
        onChange={(newValue: string) => {
          field.handleChange(newValue);
        }}
      />
    </Box>
  );
};

const EditDashboardName = ({ onValidationChange }: EditDashboardNameProps) => {
  const { dashboardName, setDashboardName, initialDashboardName } = useEditDashboardDraft();
  const { data: dashboardData } = useDashboard(false);

  // Get existing dashboard names (excluding the current dashboard being edited)
  const existingNames = useMemo(() => {
    if (!dashboardData) return [];
    return Object.keys(dashboardData).filter(name => name !== initialDashboardName);
  }, [dashboardData, initialDashboardName]);

  // Create schema with existing names for validation
  const DashboardNameSchema = useMemo(
    () =>
      z.object({
        name: createNameSchema('Dashboard name', NAME_MAX_LENGTH, existingNames)
      }),
    [existingNames]
  );

  const form = useForm({
    defaultValues: {
      name: dashboardName ?? ''
    },
    validators: {
      onChange: DashboardNameSchema,
      onMount: DashboardNameSchema
    }
  });

  return (
    <form.Field name='name'>
      {field => (
        <EditDashboardNameFieldContent
          field={field}
          setDashboardName={setDashboardName}
          onValidationChange={onValidationChange}
        />
      )}
    </form.Field>
  );
};

export default EditDashboardName;
